package org.matthewo.pages.event.tabs

import emotion.react.css
import io.ktor.client.plugins.resources.*
import io.ktor.client.request.*
import io.ktor.http.*
import kotlinx.coroutines.launch
import org.matthewo.api.ADVANCEMENT_SORT
import org.matthewo.api.EventAdvancement
import org.matthewo.api.TeamAdvancementPoints
import org.matthewo.client
import org.matthewo.hooks.MutableState
import org.matthewo.hooks.state
import org.matthewo.math.toOrdinal
import org.matthewo.pages.event.EventTabFC
import org.matthewo.pages.team.tabs.toTeam
import org.matthewo.routes.API
import org.matthewo.styles.FONT_COLOR
import org.matthewo.styles.TITLE_FONT_COLOR
import org.matthewo.table.Column
import org.matthewo.table.Order
import org.matthewo.table.Table
import react.dom.html.ReactHTML.option
import react.dom.html.ReactHTML.select
import react.useMemo
import react.useState
import tanstack.react.router.useNavigate
import web.cssom.*

private val AWARDS_POINTS = [0, 3, 6, 12, 15, 30, 60]

private val AdvancementTable = Table<TeamAdvancementPoints, AdvancementPointsTableConfig>(
    pinHeader = true, pinFirstColumn = true, sortQuery = true, initialSortColumn = 6,
    Column("", { i, _ -> (i + 1).toOrdinal() }, null, Order.ASCENDING),
    Column("Team", { "${it.number} - ${it.name}" }, compareBy { it.number }, Order.ASCENDING) { (_, _, advancing, worldsAdvancing), entry ->
        textDecoration = TextDecoration.underline
        color = when {
            entry.alreadyAdvanced -> NamedColor.gray
            entry in worldsAdvancing -> TITLE_FONT_COLOR
            entry in advancing -> NamedColor.yellow
            else -> FONT_COLOR
        }
    },
    Column("Qual", { it.advancement.qual }, compareBy { it.advancement.qual }, Order.DESCENDING),
    Column("Alliance", { it.advancement.alliance }, compareBy { it.advancement.alliance }, Order.DESCENDING),
    Column("Playoff", { it.advancement.playoff }, compareBy { it.advancement.playoff }, Order.DESCENDING),
    Column("Award", { it.advancement.award }, compareBy { it.advancement.award }, Order.DESCENDING) { (year, eventCode, _, _, editingState), entry, text ->
        var editingState by editingState
        val editing = editingState?.number == entry.number
        onDoubleClick = { event ->
            event.preventDefault()
            event.stopPropagation()
            editingState = if (editing) null else entry
        }
        if (editing) select {
            css {
                height = 100.pct
                color = Globals.inherit
                backgroundColor = Globals.inherit
                fontFamily = Globals.inherit
                fontSize = 1.em
                border = None.none
            }
            AWARDS_POINTS.forEach { value ->
                option {
                    +value.toString()
                }
            }
            value = text ?: 0
            onChange = { event ->
                val value = event.target.value
                if (null != year && null != eventCode) {
                    client.launch {
                        val response = client.post(API.Event.SetAward(year, eventCode, entry.number)) {
                            setBody(value)
                        }
                        if (HttpStatusCode.OK == response.status) {
                            val team = editingState
                            editingState = team?.let { it.copy(advancement = it.advancement.copy(award = value.toInt())) }
                        }
                    }
                }
            }
        }
        else +(text ?: "")
    },
    Column("Total", { it.advancement.total }, ADVANCEMENT_SORT, Order.DESCENDING),
)

val EventAdvancementPointsTable = EventTabFC<API.Event.Advancement, EventAdvancement>({
    API.Event.Advancement(it)
}) { (coroutineScope, year, eventCode, data) ->
    val navigate = useNavigate()

    val editing = useState<TeamAdvancementPoints>()
    val (editingTeam, _) = editing

    var teams by useState<List<TeamAdvancementPoints>>([])
    useMemo(data?.teams) {
        teams = data?.teams ?: []
    }
    useMemo(editingTeam) {
        if (null != editingTeam && editingTeam !in teams) {
            val removed = teams.filter { it.number != editingTeam.number }
            teams = removed + editingTeam
        }
    }

    val sorted = teams.filter { !it.alreadyAdvanced }.sortedWith(ADVANCEMENT_SORT.reversed())
    val worldsAdvancing = sorted.take(data?.worldsSlots ?: 0).toSet()
    val advancing = sorted.take(data?.slots ?: 0).drop(data?.worldsSlots ?: 0).toSet()

    AdvancementTable {
        entries = teams
        onClick = { column, ranking ->
            when (column.title) {
                "Team" -> navigate {
                    toTeam(year, ranking.number, eventCode)
                }
            }
        }
        config = AdvancementPointsTableConfig(year, eventCode, advancing, worldsAdvancing, editing.state)
    }
}

private data class AdvancementPointsTableConfig(
    val year: Int?,
    val eventCode: String?,
    val slots: Set<TeamAdvancementPoints>,
    val worldsSlots: Set<TeamAdvancementPoints>,
    val editing: MutableState<TeamAdvancementPoints?>,
)
